import React, { MutableRefObject } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import {
    useTheme,
    CoreAlert,
    CoreTypography
} from '@thriveglobal/thrive-web-leafkit'
import { Grid, Grow, Link, AlertTitle, Box } from '@mui/material'
import { ROUTES } from '../../routes'
import SignInTextField from '../../components/elements/SignInTextField'
import { MaxInputLength } from '../../enums/index'
import { Controller, useFormContext } from 'react-hook-form'
import { emailRegex } from '../../utils/validations'
import useTokenExchange from '../../POC/useTokenExchange'

interface EmailSignInForm {
    canChangeEmail?: boolean
    onEmailChange: (email: string) => void
    showPasswordField: boolean
    onPasswordChange: (email: string) => void
    passwordValidationError: boolean
    eligibilityValidationError: boolean
    inviteRevokedError: boolean
    passwordInputRef: MutableRefObject<HTMLInputElement>
}

interface ISigninFormInputs {
    email: string
    password?: string
}

const messages = defineMessages({
    inviteRevokedTitle: {
        defaultMessage: 'Your account is no longer active.',
        description:
            'Title of the error displayed when the user tries to sign in, but is not eligible to sign in'
    },
    inviteRevoked: {
        defaultMessage:
            'Your access to Thrive has been deactivated. If you believe this is a mistake or need assistance, please contact Thrive Support for help.',
        description:
            'Displayed when the user tries to sign in, but is not eligible to sign in'
    },
    userIneligibleTitle: {
        defaultMessage: "We couldn't verify your eligibility",
        description:
            'Title of the error displayed when the user tries to sign in, but is not eligible to sign in'
    },
    userIneligible: {
        defaultMessage:
            "It looks like you're not currently eligible to access Thrive thorugh your organization. Try again later, or contact your Thrive company admin for assistance",
        description:
            'Displayed when the user tries to sign in, but is not eligible to sign in'
    },
    emailInputLabelRequired: {
        defaultMessage: 'Email address (required)',
        description: 'Label for email address input field'
    },
    emailInputHelperText: {
        defaultMessage: 'Enter the email you used to sign up.',
        description: 'Helper text for the email input field'
    },
    emailRequired: {
        defaultMessage: 'Enter email',
        description: 'Displayed when email input was not entered'
    },
    emailInputError: {
        defaultMessage: 'Please enter a valid email address.',
        description: 'Displayed when an invalid email address is entered'
    },
    passwordInputLabel: {
        defaultMessage: 'Password',
        description: 'Label for password input field'
    },
    passwordInputLabelRequired: {
        defaultMessage: 'Password (required)',
        description: 'Label for password input field'
    },
    passwordInputError: {
        defaultMessage: 'Invalid username or password.',
        description:
            'Displayed when the user entered an inavlid username or password'
    },
    passwordRequired: {
        defaultMessage: 'Enter password',
        description: 'Displayed when password input was not entered'
    }
})

const SignInErrorAlert = ({
    title,
    message
}: {
    title: string
    message: string
}) => {
    return (
        <CoreAlert
            severity="error"
            sx={{
                width: { xs: '100%', md: '36rem' },
                py: 2,
                ml: 2,
                mt: 3,
                '& .MuiAlert-icon': {
                    fontSize: '2.5rem'
                },
                '& .MuiAlertTitle-root': {
                    fontSize: '1.6rem',
                    lineHeight: '3.2rem'
                }
            }}
        >
            <AlertTitle>{title}</AlertTitle>
            <CoreTypography variant="body2" color="text.primary">
                {message}
            </CoreTypography>
        </CoreAlert>
    )
}

const EmailSignInForm: React.FC<EmailSignInForm> = ({
    canChangeEmail = true,
    onEmailChange,
    onPasswordChange,
    showPasswordField,
    passwordValidationError,
    eligibilityValidationError,
    inviteRevokedError,
    passwordInputRef
}) => {
    const theme = useTheme()
    const intl = useIntl()

    useTokenExchange()

    const {
        control,
        formState: { errors }
    } = useFormContext<ISigninFormInputs>()

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        const newEmail = newValue.trim()
        onEmailChange(newEmail)
    }

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const password = event.target.value
        onPasswordChange(password)
    }

    return (
        <>
            {eligibilityValidationError && (
                <SignInErrorAlert
                    title={intl.formatMessage(messages.userIneligibleTitle)}
                    message={intl.formatMessage(messages.userIneligible)}
                />
            )}
            {inviteRevokedError && (
                <SignInErrorAlert
                    title={intl.formatMessage(messages.inviteRevokedTitle)}
                    message={intl.formatMessage(messages.inviteRevoked)}
                />
            )}
            <Grid
                item
                xs={12}
                sx={{
                    mt: 3
                }}
            >
                <Controller
                    render={({ field }) => (
                        <SignInTextField
                            disabled={!canChangeEmail}
                            required
                            autoComplete="email"
                            type="email"
                            spellCheck="false"
                            id="email-input"
                            color="info"
                            variant="outlined"
                            size="large"
                            value={field.value}
                            inputRef={field.ref}
                            onChange={(e) => {
                                field.onChange(e)
                                handleEmailChange(e)
                            }}
                            error={!!errors?.email}
                            helperText={errors?.email?.message}
                            label={intl.formatMessage(
                                messages.emailInputLabelRequired
                            )}
                            inputProps={{
                                maxLength: MaxInputLength.Any
                            }}
                            sx={{
                                mb: { xs: 3, md: 1 }
                            }}
                        />
                    )}
                    name="email"
                    rules={{
                        required: intl.formatMessage(messages.emailRequired),
                        pattern: {
                            value: emailRegex,
                            message: intl.formatMessage(
                                messages.emailInputError
                            )
                        }
                    }}
                    control={control}
                />
                <Grid
                    item
                    xs={12}
                    sx={{
                        mt: 3
                    }}
                >
                    <Grow
                        in={showPasswordField}
                        style={{ transformOrigin: '0 0 0' }}
                        {...(showPasswordField ? { timeout: 1000 } : {})}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                [theme.breakpoints.down('sm')]: {
                                    width: '100%'
                                }
                            }}
                        >
                            <Controller
                                render={({ field }) => (
                                    <SignInTextField
                                        required
                                        fullWidth
                                        autoComplete="current-password"
                                        id="password"
                                        color="info"
                                        variant="outlined"
                                        size="large"
                                        value={field.value}
                                        inputRef={(element) => {
                                            passwordInputRef.current = element
                                            field.ref(element)
                                        }}
                                        onChange={(e) => {
                                            field.onChange(e)
                                            handlePasswordChange(e)
                                        }}
                                        error={
                                            !!errors?.password ||
                                            passwordValidationError
                                        }
                                        label={intl.formatMessage(
                                            messages.passwordInputLabelRequired
                                        )}
                                        type="password"
                                        helperText={
                                            passwordValidationError
                                                ? intl.formatMessage(
                                                      messages.passwordInputError
                                                  )
                                                : errors?.password?.message
                                        }
                                        FormHelperTextProps={{
                                            variant: 'standard'
                                        }}
                                        inputProps={{
                                            maxLength: MaxInputLength.Password
                                        }}
                                        sx={{
                                            mr: '1.6rem',
                                            mb: 0,
                                            [theme.breakpoints.down('sm')]: {
                                                mt: '2rem'
                                            }
                                        }}
                                    />
                                )}
                                name="password"
                                rules={{
                                    required: intl.formatMessage(
                                        messages.passwordRequired
                                    )
                                }}
                                control={control}
                            />
                            <Link
                                component={RouterLink}
                                to={ROUTES.FORGOT_PASSWORD}
                                variant="body2"
                                color="primary.contrastText"
                                sx={{ pt: '.5rem' }}
                            >
                                <FormattedMessage
                                    description="Link to forgot password page"
                                    defaultMessage="Forgot password?"
                                />
                            </Link>
                        </Box>
                    </Grow>
                </Grid>
            </Grid>
        </>
    )
}

export default EmailSignInForm
